// src/pages/Catalogue.tsx — Item catalogue and warehouse stock levels
import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_CATALOGUE, MOCK_SUPPLIERS, MOCK_STOCK } from '../utils/mockData'
import { formatCurrency, statusMeta } from '../utils/formatters'
import { StatusBadge } from '../components/StatusBadge'
import {
  Package, Plus, Search, X, AlertTriangle, CheckCircle,
  ChevronDown, ChevronUp, Layers, ShoppingCart,
} from 'lucide-react'
import { addDraftPO } from '../utils/workflowEvents'

type CatItem  = typeof MOCK_CATALOGUE[0]
type StockRow = typeof MOCK_STOCK[0]

const MT_STYLE: Record<string, { bg: string; text: string; label: string }> = {
  BUILD_MATERIAL:    { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Build material' },
  CONSUMABLE:        { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Consumable' },
  CONTEXT_DEPENDENT: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Context dependent' },
}

const CABLE_CAT_STYLE: Record<string, { bg: string; text: string }> = {
  POWER: { bg: 'bg-red-100',  text: 'text-red-700' },
  COMMS: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
}

// ── Add Item Modal ─────────────────────────────────────────────────
function AddItemModal({ onClose, onAdd }: { onClose: () => void; onAdd: (item: any) => void }) {
  const [form, setForm] = useState({
    code: '', description: '', materialType: '' as string,
    cableCategory: '' as string,
    unit: 'EA', unitCost: '', supplierId: MOCK_SUPPLIERS[0].id,
  })
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))
  const isCable = form.code.toUpperCase().startsWith('CAB') || form.description.toLowerCase().includes('cable')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.materialType) return
    onAdd({
      id: `i_new_${Date.now()}`,
      code: form.code.toUpperCase(),
      description: form.description,
      materialType: form.materialType,
      cableCategory: form.cableCategory || null,
      unit: form.unit,
      unitCost: parseFloat(form.unitCost),
      supplierId: form.supplierId,
      supplierName: MOCK_SUPPLIERS.find(s => s.id === form.supplierId)?.name ?? '',
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900">Add catalogue item</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Item code <span className="text-red-500">*</span></label>
              <input className="input uppercase" value={form.code} onChange={e => set('code', e.target.value)} required placeholder="e.g. CAB-PWR-6MM" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Unit</label>
              <select className="input" value={form.unit} onChange={e => set('unit', e.target.value)}>
                {['EA','m','kg','l','bag','roll','set','pair'].map(u => <option key={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description <span className="text-red-500">*</span></label>
            <input className="input" value={form.description} onChange={e => set('description', e.target.value)} required placeholder="Full item description" />
          </div>

          {/* Business rule: materialType REQUIRED — no default */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Material type <span className="text-red-500">*</span>
              <span className="text-gray-400 font-normal ml-1">(required — no default)</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(MT_STYLE).map(([k, v]) => (
                <button key={k} type="button" onClick={() => set('materialType', k)}
                  className={`p-2.5 rounded-lg border-2 text-xs font-semibold transition-colors ${form.materialType === k ? `border-current ${v.bg} ${v.text}` : 'border-gray-200 text-gray-400 hover:border-gray-300'}`}>
                  {v.label}
                </button>
              ))}
            </div>
            {!form.materialType && <p className="text-xs text-red-500 mt-1">Material type must be set explicitly</p>}
          </div>

          {isCable && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Cable category (if cable)</label>
              <div className="flex gap-2">
                {['POWER','COMMS'].map(c => (
                  <button key={c} type="button" onClick={() => set('cableCategory', form.cableCategory === c ? '' : c)}
                    className={`px-3 py-1.5 rounded-lg border-2 text-xs font-semibold transition-colors ${form.cableCategory === c ? `${CABLE_CAT_STYLE[c].bg} ${CABLE_CAT_STYLE[c].text} border-current` : 'border-gray-200 text-gray-400 hover:border-gray-300'}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Unit cost (ex GST)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input type="number" step="0.01" className="input pl-7" value={form.unitCost} onChange={e => set('unitCost', e.target.value)} required placeholder="0.00" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Preferred supplier</label>
              <select className="input" value={form.supplierId} onChange={e => set('supplierId', e.target.value)}>
                {MOCK_SUPPLIERS.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={!form.materialType} className="btn-primary">Add item</button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Catalogue tab ─────────────────────────────────────────────────
function CatalogueTab({ items, onAdd, canEdit }: { items: CatItem[]; onAdd: (item: any) => void; canEdit: boolean }) {
  const [search, setSearch]         = useState('')
  const [typeFilter, setTypeFilter] = useState('ALL')
  const [showAdd, setShowAdd]       = useState(false)
  const [sort, setSort]             = useState<'code'|'desc'|'cost'>('code')
  const [sortDir, setSortDir]       = useState<'asc'|'desc'>('asc')

  const SortBtn = ({ col, label }: { col: typeof sort; label: string }) => (
    <button className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-900"
      onClick={() => { if (sort === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSort(col); setSortDir('asc') } }}>
      {label}
      {sort === col ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ChevronDown className="w-3 h-3 opacity-30" />}
    </button>
  )

  const filtered = items
    .filter(i => {
      const matchSearch = !search || i.code.toLowerCase().includes(search.toLowerCase()) || i.description.toLowerCase().includes(search.toLowerCase())
      const matchType   = typeFilter === 'ALL' || i.materialType === typeFilter
      return matchSearch && matchType
    })
    .sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1
      if (sort === 'code') return a.code.localeCompare(b.code) * dir
      if (sort === 'desc') return a.description.localeCompare(b.description) * dir
      if (sort === 'cost') return (a.unitCost - b.unitCost) * dir
      return 0
    })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input className="input pl-9" placeholder="Search code or description…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-1.5">
          {['ALL','BUILD_MATERIAL','CONSUMABLE'].map(f => (
            <button key={f} onClick={() => setTypeFilter(f)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${typeFilter === f ? 'bg-brand-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {f === 'ALL' ? 'All' : f === 'BUILD_MATERIAL' ? 'Build materials' : 'Consumables'}
            </button>
          ))}
        </div>
        {canEdit && (
          <>
            <CatalogueImportButton onImportMany={newItems => newItems.forEach(item => onAdd(item))} />
            <button onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-2 text-xs">
              <Plus className="w-3.5 h-3.5" /> Add item
            </button>
          </>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b bg-gray-50">
              <th className="px-4 py-3"><SortBtn col="code" label="Code" /></th>
              <th className="px-3 py-3"><SortBtn col="desc" label="Description" /></th>
              <th className="px-3 py-3 text-xs font-medium text-gray-500">Type</th>
              <th className="px-3 py-3 text-xs font-medium text-gray-500">Cable</th>
              <th className="px-3 py-3 text-xs font-medium text-gray-500">Unit</th>
              <th className="px-3 py-3"><SortBtn col="cost" label="Unit cost" /></th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500">Supplier</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map(item => {
              const mt = MT_STYLE[item.materialType]
              const cc = item.cableCategory ? CABLE_CAT_STYLE[item.cableCategory] : null
              return (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-500 whitespace-nowrap">{item.code}</td>
                  <td className="px-3 py-3 text-gray-900">{item.description}</td>
                  <td className="px-3 py-3">
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${mt.bg} ${mt.text}`}>{mt.label}</span>
                  </td>
                  <td className="px-3 py-3">
                    {cc && <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${cc.bg} ${cc.text}`}>{item.cableCategory}</span>}
                  </td>
                  <td className="px-3 py-3 text-gray-500">{item.unit}</td>
                  <td className="px-3 py-3 tabular-nums font-medium">{formatCurrency(item.unitCost, 2)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{item.supplierName}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="py-12 text-center text-gray-400 text-sm">No items match your search</div>
        )}
      </div>

      <div className="text-xs text-gray-400 pt-1">{filtered.length} items shown</div>

      {showAdd && <AddItemModal onClose={() => setShowAdd(false)} onAdd={item => { onAdd(item); setShowAdd(false) }} />}
    </div>
  )
}

// ── Stock tab ─────────────────────────────────────────────────────
function StockTab({ stock }: { stock: StockRow[] }) {
  const [search, setSearch]       = useState('')
  const [locFilter, setLocFilter] = useState('ALL')
  const [raisedPOs, setRaisedPOs] = useState<Set<string>>(new Set())
  const [lastPO, setLastPO]       = useState<{ code: string; poRef: string } | null>(null)

  const handleRaisePO = (row: StockRow) => {
    const catItem    = MOCK_CATALOGUE.find(i => i.id === row.itemId)
    const poRef      = `PO-STOCK-${row.code.replace(/[^A-Z0-9]/g,'-')}-${Date.now().toString().slice(-4)}`
    const reorderQty = Math.max((row.reorderLevel * 2) - row.qtyOnHand, row.reorderLevel)
    addDraftPO({
      poRef,
      rfqRef:     '',
      jobRef:     'WAREHOUSE',
      supplier:   catItem?.supplierName ?? 'Preferred supplier',
      totalExGST: reorderQty * (catItem?.unitCost ?? 0),
      createdAt:  new Date().toISOString(),
    })
    setRaisedPOs(prev => new Set([...prev, row.id]))
    setLastPO({ code: row.code, poRef })
    setTimeout(() => setLastPO(null), 6000)
  }

  const locations = Array.from(new Set(stock.map(s => s.location)))

  const filtered = stock.filter(s => {
    const matchSearch = !search || s.code.toLowerCase().includes(search.toLowerCase()) || s.description.toLowerCase().includes(search.toLowerCase())
    const matchLoc    = locFilter === 'ALL' || s.location === locFilter
    return matchSearch && matchLoc
  })

  const alerts    = filtered.filter(s => s.qtyOnHand <= s.reorderLevel)
  const outOfStock = filtered.filter(s => s.qtyOnHand === 0)

  return (
    <div className="space-y-4">
      {lastPO && (
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-800">
          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
          <span>Draft PO <strong className="font-mono">{lastPO.poRef}</strong> created for <strong>{lastPO.code}</strong> — go to Purchase Orders to review and approve.</span>
          <button onClick={() => setLastPO(null)} className="ml-auto text-green-600 hover:text-green-800 text-lg leading-none">×</button>
        </div>
      )}
      {outOfStock.length > 0 && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-800">
            <strong>{outOfStock.length} item{outOfStock.length !== 1 ? 's' : ''} out of stock:</strong>{' '}
            {outOfStock.map(s => s.code).join(', ')}
          </p>
        </div>
      )}
      {alerts.length > 0 && outOfStock.length === 0 && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            <strong>{alerts.length} item{alerts.length !== 1 ? 's' : ''} at or below reorder level</strong> — click Raise PO on each item to create a draft purchase order
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input className="input pl-9" placeholder="Search stock items…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="input text-sm w-auto" value={locFilter} onChange={e => setLocFilter(e.target.value)}>
          <option value="ALL">All locations</option>
          {locations.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b bg-gray-50 text-xs text-gray-500">
              <th className="px-4 py-3 font-medium">Code</th>
              <th className="px-3 py-3 font-medium">Description</th>
              <th className="px-3 py-3 font-medium">Location</th>
              <th className="px-3 py-3 font-medium text-right">On hand</th>
              <th className="px-3 py-3 font-medium text-right">Reorder level</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map(row => {
              const isOut   = row.qtyOnHand === 0
              const isLow   = !isOut && row.qtyOnHand <= row.reorderLevel
              const isOK    = !isOut && !isLow
              return (
                <tr key={row.id} className={`hover:bg-gray-50 ${isOut ? 'bg-red-50' : isLow ? 'bg-amber-50' : ''}`}>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{row.code}</td>
                  <td className="px-3 py-3 text-gray-900">{row.description}</td>
                  <td className="px-3 py-3 text-xs text-gray-500">{row.location}</td>
                  <td className={`px-3 py-3 text-right tabular-nums font-bold ${isOut ? 'text-red-600' : isLow ? 'text-amber-600' : 'text-gray-900'}`}>
                    {row.qtyOnHand} <span className="font-normal text-gray-400">{row.unit}</span>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-gray-500">
                    {row.reorderLevel} <span className="text-gray-400">{row.unit}</span>
                  </td>
                  <td className="px-4 py-3">
                    {isOut  && <span className="text-xs font-medium bg-red-100 text-red-700 px-2 py-0.5 rounded-full flex items-center gap-1 w-fit"><AlertTriangle className="w-3 h-3" />Out of stock</span>}
                    {isLow  && <span className="text-xs font-medium bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1 w-fit"><AlertTriangle className="w-3 h-3" />Low stock</span>}
                    {isOK   && <span className="text-xs font-medium bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1 w-fit"><CheckCircle className="w-3 h-3" />In stock</span>}
                  </td>
                  <td className="px-4 py-3">
                    {(isOut || isLow) && !raisedPOs.has(row.id) && (
                      <button onClick={() => handleRaisePO(row)}
                        className="text-xs bg-[#1F3864] text-white hover:bg-[#2E75B6] px-2.5 py-1 rounded-lg font-medium flex items-center gap-1 transition-colors">
                        <ShoppingCart className="w-3 h-3" />Raise PO
                      </button>
                    )}
                    {raisedPOs.has(row.id) && (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />PO raised
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-gray-400">{filtered.length} lines · {alerts.length} alert{alerts.length !== 1 ? 's' : ''}</div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────


function downloadCatalogueTemplate() {
  const rows = [
    ['Code','Description','Material Type','Cable Category','Unit','Unit Cost','Supplier Name'],
    ['CAB-PWR-16MM','16mm Twin + Earth Power Cable','BUILD_MATERIAL','POWER','m','9.40','Rexel Electrical'],
    ['PPE-GLOVES-CL0','Insulated Gloves Class 00','CONSUMABLE','','PR','38.00','Blackwoods Safety'],
  ]
  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join(String.fromCharCode(10))
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href = url; a.download = 'MCB_Catalogue_Import_Template.csv'
  document.body.appendChild(a); a.click()
  document.body.removeChild(a); URL.revokeObjectURL(url)
}


function parseCatCSVLine(line: string): string[] {
  const out: string[] = []
  let cur = '', inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      if (inQuotes && line[i+1] === '"') { cur += '"'; i++ }
      else inQuotes = !inQuotes
    } else if (ch === ',' && !inQuotes) {
      out.push(cur); cur = ''
    } else cur += ch
  }
  out.push(cur)
  return out.map(s => s.trim())
}


function parseCatalogueCSV(text: string): { items: any[]; errors: string[] } {
  const rows = text.split(/\r?\n/).filter(r => r.trim().length > 0)
  if (rows.length < 2) return { items: [], errors: ['File is empty or has no data rows'] }
  const header = parseCatCSVLine(rows[0]).map(h => h.toLowerCase())
  const idx = {
    code: header.indexOf('code'), desc: header.indexOf('description'),
    type: header.indexOf('material type'), cat: header.indexOf('cable category'),
    unit: header.indexOf('unit'), cost: header.indexOf('unit cost'),
    sup: header.indexOf('supplier name'),
  }
  const errors: string[] = []
  const items: any[] = []
  for (let i = 1; i < rows.length; i++) {
    const cells = parseCatCSVLine(rows[i])
    const code = cells[idx.code]?.trim()
    const desc = cells[idx.desc]?.trim()
    const unitCost = parseFloat(cells[idx.cost])
    if (!code || !desc) { errors.push(`Row ${i+1}: missing code or description — skipped`); continue }
    if (isNaN(unitCost)) { errors.push(`Row ${i+1}: invalid unit cost — skipped`); continue }
    items.push({
      id: `cat_imported_${Date.now()}_${i}`,
      code, description: desc,
      materialType: cells[idx.type]?.trim().toUpperCase() || 'BUILD_MATERIAL',
      cableCategory: cells[idx.cat]?.trim() || null,
      unit: cells[idx.unit]?.trim() || 'EA',
      unitCost, supplierId: null,
      supplierName: cells[idx.sup]?.trim() || '—',
    })
  }
  return { items, errors }
}


function CatalogueImportButton({ onImportMany }: { onImportMany: (items: any[]) => void }) {
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState<{ count: number; errors: string[] } | null>(null)

  const handleFile = (file: File) => {
    setImporting(true)
    const reader = new FileReader()
    reader.onload = e => {
      const text = String(e.target?.result ?? '')
      const { items, errors } = parseCatalogueCSV(text)
      if (items.length > 0) onImportMany(items)
      setResult({ count: items.length, errors })
      setImporting(false)
    }
    reader.onerror = () => { setResult({ count: 0, errors: ['Failed to read file'] }); setImporting(false) }
    reader.readAsText(file)
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button onClick={downloadCatalogueTemplate} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg font-medium">
        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
        Download template
      </button>
      <label className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-indigo-200 hover:bg-indigo-50 text-indigo-700 rounded-lg font-medium cursor-pointer">
        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
        {importing ? 'Importing…' : 'Bulk import CSV'}
        <input type="file" accept=".csv" className="hidden" disabled={importing}
          onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = '' }} />
      </label>
      {result && (
        <span className={`text-xs font-medium ${result.errors.length > 0 ? 'text-amber-600' : 'text-green-600'}`}>
          {result.count > 0 ? `✓ ${result.count} item${result.count!==1?'s':''} imported` : ''}
          {result.errors.length > 0 ? ` · ${result.errors.length} skipped` : ''}
        </span>
      )}
    </div>
  )
}
export function Catalogue() {
  const { isTier } = useAuthStore()
  const canEdit = isTier('C_PROCUREMENT')
  const [items, setItems] = useState<CatItem[]>(MOCK_CATALOGUE)
  const [tab, setTab]     = useState<'catalogue'|'stock'>('catalogue')

  const stockAlerts = MOCK_STOCK.filter(s => s.qtyOnHand <= s.reorderLevel).length

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Catalogue & Stock</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {items.length} catalogue items
            {stockAlerts > 0 && <span className="ml-2 text-amber-600 font-medium">· {stockAlerts} stock alert{stockAlerts !== 1 ? 's' : ''}</span>}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        <button onClick={() => setTab('catalogue')}
          className={`text-sm font-medium px-5 py-2 rounded-lg transition-colors flex items-center gap-2 ${tab === 'catalogue' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          <Package className="w-4 h-4" /> Item catalogue
        </button>
        <button onClick={() => setTab('stock')}
          className={`text-sm font-medium px-5 py-2 rounded-lg transition-colors flex items-center gap-2 ${tab === 'stock' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          <Layers className="w-4 h-4" /> Warehouse stock
          {stockAlerts > 0 && <span className="bg-amber-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{stockAlerts}</span>}
        </button>
      </div>

      {/* Tab content */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        {tab === 'catalogue'
          ? <CatalogueTab items={items} canEdit={canEdit} onAdd={item => setItems(prev => [...prev, item])} />
          : <StockTab stock={MOCK_STOCK} />
        }
      </div>
    </div>
  )
}
